import Button from 'modules/shared-components/button/button';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { valueAsSafeNumber } from 'utils/FormatNumber';
import { useWalletsSearchFilters } from './usePageParams';

import { PrimaryButton } from 'modules/shared-components/button/SubmitButton';
import {
  PresetButton,
  PresetButtonDescription,
  PresetButtonTitle,
} from '../TelegramCalls/PresetButton';
import NumberInput from '../../../components/inputs/number-input';
import CustomModal from '../../../components/modal/CustomModal';

type Props = {
  handleClose: () => void;
};
export const schema = yup.object({
  min: yup.number().min(0),
  max: yup.number().min(0),
});

type FormState = yup.InferType<typeof schema>;

export function ReturnsFilterModal({ handleClose }: Props) {
  const { filtersState, setFilters } = useWalletsSearchFilters();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormState>({
    resolver: yupResolver(schema),
    defaultValues: {
      min: filtersState?.minReturns ? filtersState.minReturns * 100 : undefined,
      max: filtersState?.maxReturns ? filtersState.maxReturns * 100 : undefined,
    },
  });

  return (
    <CustomModal
      title="Total Returns Range"
      handleClose={handleClose}
      showModal
    >
      <form
        autoComplete="off"
        className="contents"
        onSubmit={(e) => {
          void handleSubmit((form) => {
            setFilters({
              ...filtersState,
              minReturns: form.min ? form.min / 100 : undefined,
              maxReturns: form.max ? form.max / 100 : undefined,
            });
            handleClose();
          })(e);
        }}
      >
        <CustomModal.Body className="xxs:p-4">
          <CustomModal.Title className="hidden lg:flex">
            Total Returns Range
          </CustomModal.Title>

          <CustomModal.Content>
            <div className="xxs:space-y-3">
              <p className="xxs:text-sm xxs:mb-0">
                The Total Realized Returns Range filter lets you focus on
                wallets with a specific amount of returns (in %) over the last
                30 days. The returns are calculated as total realized PnL
                divided by the maximum amount of capital employed in open trades
                over the last 30 days. This feature can help you identify the
                most successful wallets when looking at their realized returns
                in the last 30 days.
              </p>
              <div className="xxs:space-y-1">
                <p className="xxs:text-sm xxs:mb-0">Set a Range</p>
                <div className="grid grid-cols-3 xxs:gap-1 xxs:mb-4">
                  <PresetButton
                    onClick={() => {
                      setValue('min', undefined);
                      setValue('max', 0.3 * 100);
                    }}
                  >
                    <PresetButtonTitle>👌 Medium</PresetButtonTitle>
                    <PresetButtonDescription>0% to 30%</PresetButtonDescription>
                  </PresetButton>
                  <PresetButton
                    onClick={() => {
                      setValue('min', 0.3 * 100);
                      setValue('max', 9 * 100);
                    }}
                  >
                    <PresetButtonTitle>👍 Good</PresetButtonTitle>
                    <PresetButtonDescription>
                      +30% to 10X
                    </PresetButtonDescription>
                  </PresetButton>
                  <PresetButton
                    onClick={() => {
                      setValue('min', 9 * 100);
                      setValue('max', undefined);
                    }}
                  >
                    <PresetButtonTitle>🚀 High</PresetButtonTitle>
                    <PresetButtonDescription>Above 10X</PresetButtonDescription>
                  </PresetButton>
                </div>
                <p className="xxs:mb-1">Custom</p>
                <div className="flex xxs:gap-1">
                  <NumberInput
                    label="Min"
                    suffix="%"
                    {...register('min', {
                      setValueAs: valueAsSafeNumber,
                    })}
                    error={errors.min?.message}
                  />
                  <NumberInput
                    label="Max"
                    suffix="%"
                    {...register('max', {
                      setValueAs: valueAsSafeNumber,
                    })}
                    error={errors.max?.message}
                  />
                </div>
              </div>
            </div>
          </CustomModal.Content>
        </CustomModal.Body>
        <CustomModal.Footer>
          <PrimaryButton type="submit" className="xxs:text-base lg:text-xs">
            SAVE
          </PrimaryButton>
          <Button
            className="xxs:text-base lg:text-xs"
            type="button"
            variant="dexNeutral"
            onClick={() => handleClose()}
          >
            Close
          </Button>
        </CustomModal.Footer>
      </form>
    </CustomModal>
  );
}
